import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Images, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ALBUMS } from "@/data/photos";

export default function GalleryPreview() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const navigate = useNavigate();

  return (
    /* @section: gallery-preview */
    <section className="py-24 bg-background" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-12"
        >
          <div>
            <span className="inline-block text-accent text-sm font-semibold uppercase tracking-[0.15em] mb-3">Photos & albums</span>
            <h2 className="text-4xl lg:text-5xl font-bold text-foreground" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Notre Galerie
            </h2>
          </div>
          <button
            onClick={() => { navigate("/galerie"); window.scrollTo({ top: 0 }); }}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border text-sm font-semibold text-foreground hover:border-accent/40 hover:text-accent transition-all duration-200"
          >
            <Images className="w-4 h-4" />
            Voir tous les albums
            <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>

        {/* Albums grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {ALBUMS.map((album, i) => {
            const routes: Record<string, string> = {
              portraits: "/fondatrice",
              ceremonies: "/ceremonies",
              actions: "/actions",
              events: "/actions",
              equipe: "/equipe",
            };
            return (
              <motion.button
                key={album.id}
                initial={{ opacity: 0, y: 28 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.08 * i }}
                onClick={() => { navigate(routes[album.id]); window.scrollTo({ top: 0 }); }}
                className="group relative rounded-3xl overflow-hidden aspect-[3/4] text-left cursor-pointer"
              >
                {/* Cover image */}
                <img
                  src={album.cover}
                  alt={album.title}
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                {/* Gradient */}
                <div className={`absolute inset-0 bg-gradient-to-t ${album.color} opacity-80 group-hover:opacity-95 transition-opacity duration-300`} />
                {/* Mini grid of photos */}
                <div className="absolute top-3 right-3 grid grid-cols-2 gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {album.photos.slice(1, 5).map((p) => (
                    <div key={p.id} className="w-8 h-8 rounded-lg overflow-hidden border border-white/30">
                      <img src={p.url} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="text-white text-base font-bold leading-tight mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {album.title}
                  </p>
                  <p className="text-white/60 text-xs">{album.photos.length} photos</p>
                  <div className="mt-3 flex items-center gap-1 text-accent text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Voir l'album <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
